import { Box, Chip, MenuItem, Select, Typography } from '@mui/material'
import "@fontsource/rubik";
import MediaThumbnail from '../../../components/media/MediaThumbnail'
import { formatHistoryDate } from '../../../utils/revisionHistory'

/**
 * Version picker for the review card, sitting above the player it controls.
 *
 * Picking a version moves the player and the notes panel together, so the
 * control belongs with the video rather than inside the notes column.
 *
 * Rendered only when there is something to choose between - a project with a
 * single delivered cut shows no picker at all.
 *
 * Each row carries a still from that cut. The history response has no thumbnail
 * field, so the frame is produced in the browser from the `video_url` already in
 * the response - the shared MediaThumbnail does the work, including falling back
 * to a muted <video> poster when a storage bucket's CORS taints the canvas, and
 * to a typed icon for a version whose file is still transferring. Frames are
 * cached per final video id, so reopening the menu costs nothing.
 */
const RevisionVersionSelect = (props) => {
  const { versions, selectedVideoId, onSelect, currentFinalVideoId } = props;

  if (!Array.isArray(versions) || versions.length < 2) return null;

  const selected = versions.find((version) => version?.final_video_id === selectedVideoId);

  return (
    <Box
      data-testid='revision-version-select-bar'
      className='flex a-center'
      gap={1}
      sx={{
        width: '100%',
        padding: { xs: '4px 10px', xl: '7px 16px' },
        borderBottom: '1px solid #0000000f',
      }}
    >
      <Typography
        sx={{ fontFamily: 'Rubik', fontWeight: 600, color: '#08231B', opacity: 0.6, flexShrink: 0, fontSize: { xs: '7px', md: '9px', lg: '9px', xl: '12px' } }}
      >
        Version
      </Typography>

      <Select
        value={selected ? selectedVideoId : ''}
        onChange={(event) => onSelect(event.target.value)}
        data-testid='revision-version-select'
        inputProps={{ 'aria-label': 'Select version' }}
        MenuProps={{
          // Select positions its menu over the selected row by default, which
          // leaves the popup offset from the control. Anchor it to the button's
          // bottom-left so the two share a left edge.
          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
          transformOrigin: { vertical: 'top', horizontal: 'left' },
          PaperProps: { sx: { maxHeight: 280, marginTop: '4px', borderRadius: '4px' } },
        }}
        renderValue={() => (
          <Box className='flex a-center' gap={0.6} sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography
              sx={{ fontFamily: 'Rubik', fontWeight: 600, whiteSpace: 'nowrap', fontSize: { xs: '7px', md: '9px', lg: '10px', xl: '13px' } }}
            >
              Version {selected?.version}
            </Typography>
            <VersionBadges version={selected} currentFinalVideoId={currentFinalVideoId} />
          </Box>
        )}
        sx={{
          fontFamily: 'Rubik',
          borderRadius: '4px',
          backgroundColor: '#ffffff',
          // Fixed, so the control does not resize as versions with different
          // labels and flags are selected.
          width: { xs: '100px', md: '114px', lg: '126px', xl: '170px' },
          flexShrink: 0,
          '& .MuiSelect-select': {
            padding: { xs: '5px 8px', md: '6px 8px', xl: '9px 14px' },
            paddingRight: '26px !important',
            // Without this the label sits against the top of the control rather
            // than on its centre line.
            display: 'flex',
            alignItems: 'center',
            minHeight: 'unset',
          },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#0000001a' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#08231B' },
          // Focus leaves the outline exactly as it was - MUI would otherwise
          // thicken and recolour it, which reads as a second border on click.
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0000001a', borderWidth: '1px' },
          // Keyboard-only, so tabbing is still visible without marking clicks.
          '& .MuiSelect-select:focus-visible': { outline: '2px solid #08231B', outlineOffset: '1px' },
          '& .MuiSelect-icon': { fontSize: { xs: '14px', xl: '20px' } },
        }}
      >
        {versions.map((version) => (
          <MenuItem
            key={version?.final_video_id}
            value={version?.final_video_id}
            data-testid='revision-version-option'
            sx={{ fontFamily: 'Rubik', paddingY: { xs: 0.5, xl: 1 } }}
          >
            <Box className='flex a-center' gap={1} sx={{ width: '100%', justifyContent: 'space-between' }}>
              <Box className='flex a-center' gap={1}>
                <Box
                  data-testid='revision-version-thumbnail'
                  sx={{
                    width: { xs: '52px', lg: '60px', xl: '82px' },
                    flexShrink: 0,
                    border: '1px solid #9e9e9e',
                    borderRadius: '4px',
                    // The thumbnail paints to its own corners; clipping here
                    // keeps it inside the frame rather than over it.
                    overflow: 'hidden',
                    lineHeight: 0,
                  }}
                >
                  <MediaThumbnail
                    file={{ video_url: version?.video_url }}
                    fileType='video'
                    cacheKey={`revision-version-${version?.final_video_id}`}
                    radius='4px'
                  />
                </Box>

                <Box className='flex a-center' gap={0.6}>
                  <Typography
                    sx={{ fontFamily: 'Rubik', fontWeight: 600, whiteSpace: 'nowrap', fontSize: { xs: '8px', md: '10px', lg: '11px', xl: '14px' } }}
                  >
                    Version {version?.version}
                  </Typography>
                  <VersionBadges version={version} currentFinalVideoId={currentFinalVideoId} />
                </Box>
              </Box>

              <Typography
                sx={{ fontFamily: 'Rubik', opacity: 0.6, whiteSpace: 'nowrap', fontSize: { xs: '7px', md: '8px', lg: '9px', xl: '12px' }, pl: 1.5 }}
              >
                {formatHistoryDate(version?.delivered_at)}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

/**
 * Flags come straight from the response - the backend does not guarantee exactly
 * one latest or accepted cut, so each is rendered on its own terms.
 */
const VersionBadges = ({ version, currentFinalVideoId }) => {
  if (!version) return null;
  const isCurrentCut = version?.final_video_id === currentFinalVideoId;
  return (
    <>
      {isCurrentCut && <VersionBadge label='Current' />}
      {version?.accepted === true && <VersionBadge label='Accepted' />}
      {version?.latest === true && !isCurrentCut && <VersionBadge label='Latest' />}
      {version?.sent_for_approval === true && <VersionBadge label='For approval' />}
    </>
  )
}

const VersionBadge = ({ label }) => (
  <Chip
    label={label}
    size='small'
    data-testid={`revision-version-badge-${label}`}
    sx={{
      height: { xs: '11px', lg: '14px', xl: '19px' },
      backgroundColor: '#e8f5ee',
      color: '#08231B',
      fontFamily: 'Rubik',
      fontWeight: 600,
      fontSize: { xs: '5px', md: '6px', lg: '7px', xl: '10px' },
      '& .MuiChip-label': { padding: { xs: '0 4px', xl: '0 7px' } },
    }}
  />
)

export default RevisionVersionSelect
